import mixpanel from "mixpanel-browser";
import * as amplitude from "@amplitude/analytics-browser";
import axios, { AxiosResponse } from "axios";
import QuestSdk, { Core } from "@questlabs/core";

import "react-toastify/dist/ReactToastify.css";
import Cookies from "universal-cookie";
import config from "./config";
const AMPLITUDE_API_KEY = "cfd03815b8ea488fb72990c319994fc";
const MIXPANEL_API_KEY = "40e0349e55b0744cbc8194805fa4c37b";
const GA4_TRACKING_ID = "G-8E6PSWXZR3";
const GA4_API_SECRET = "JYSz9eEPT-eoh2aTVDESHg";
import * as Sentry from "@sentry/react";
import { useContext, useMemo } from "react";
import QuestContext from "./components/QuestWrapper";

if (window.location.hostname !== "localhost") {
  Sentry.init({
    dsn: "https://33bf5f26eec50e6c2c556f707d4e459f@o1016721.ingest.us.sentry.io/4506992954245120",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [config.BACKEND_URL, config.BACKEND_URL_STAGING],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

interface AmplitudeUserProperties {
  userid?: string;
}

interface GA4EventPayload {
  client_id?: string;
  events: Array<{ name: string }>;
}

class General {
  private serviceType: string;
  private apiType: string;
  private apiKey: string;
  private entityId: string;
  private questSdk: any;
  private quest: any;

  constructor(
    serviceType: string,
    apiType?: string,
    apiKey?: string,
    entityId?: string
  ) {
    this.serviceType = serviceType || "mixpanel";
    this.apiType = apiType || "STAGING";
    this.apiKey = apiKey || "";
    this.entityId = entityId || "";

    mixpanel.init("52a91caf0a6518b8b8909977ab92c39a");

    this.questSdk = new QuestSdk({
      apiKey: apiKey as string,
      apiSecret: "",
      entityId: entityId,
      stagingEnvironment: this.apiType === "STAGING",
    });

    this.quest = new Core.QuestEvents(this.questSdk);
  }

  fireTrackingEvent(
    eventName: string,
    componentName: string,
    metadata?: { eventProperties?: any; userid?: string }
  ) {
    if (this.apiType === "STAGING") {
      return { success: true };
    } else {
      mixpanel.track(eventName);
      return { success: true };
    }
  }

  async getKeys({
    entityId,
    apikey,
    token,
    userid,
  }: {
    entityId: string;
    apikey: string;
    token: string;
    userid: string;
  }) {
    try {
      const response: AxiosResponse = await axios.get(
        `${config.BACKEND_URL}api/entities/${entityId}/users/${userid}/social-tokens`,
        {
          headers: { token, entityid: entityId, userid, apikey },
        }
      );
    } catch (error) {
      console.log(error);
    }
  }

  async getExternalLogin({
    apiType,
    uniqueUserId,
    entityId,
    userId,
    apiKey,
    apiSecret,
    token,
    uniqueEmailId,
    entityAuthenticationToken,
    alert = false,
  }: {
    apiType: string;
    uniqueUserId?: string;
    entityId: string;
    userId?: string;
    apiKey: string;
    apiSecret: string;
    token?: string;
    uniqueEmailId?: string;
    entityAuthenticationToken?: string;
    alert?: boolean;
  }) {
    const cookies = new Cookies();
    const headers = {
      apiKey: apiKey,
      apisecret: apiSecret,
      ...(!!entityAuthenticationToken && {
        "entity-authentication-token": entityAuthenticationToken,
      }),
      ...(!!userId && { userId: userId }),
      ...(!!token && { token: token }),
    };

    const body = {
      externalUserId: !!uniqueUserId && uniqueUserId,
      entityId: entityId,
      email: uniqueEmailId,
      ignoreMAU: true,
    };

    let questUserId = cookies.get("questUserId");
    let questUserToken = cookies.get("questUserToken");
    let externalUserId = cookies.get("externalUserId");

    if (
      !!externalUserId &&
      !!questUserId &&
      !!questUserToken &&
      (externalUserId == uniqueUserId || externalUserId == uniqueEmailId)
    ) {
      return { userId: questUserId, token: questUserToken };
    }

    let BACKEND_URL =
      apiType == "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
    let externalLogin = await axios.post(
      `${BACKEND_URL}api/users/external/login?alert=${alert}`,
      body,
      { headers }
    );
    let { userId: q_userId, token: q_token } = externalLogin.data;
    const date = new Date();
    date.setHours(date.getHours() + 48);
    cookies.set("externalUserId", uniqueUserId, {
      path: "/",
      expires: date,
    });
    cookies.set("questUserId", q_userId, { path: "/", expires: date });
    cookies.set("questUserToken", q_token, { path: "/", expires: date });
    cookies.set("externalUserId", uniqueUserId || uniqueEmailId, {
      path: "/",
      expires: date,
    });
    return { userId: q_userId, token: q_token };
  }

  captureSentryException(error: any) {
    const url = error?.response?.config?.url;
    if (url && (url.toLowerCase().includes("quest"))) {
      if (this.apiType === "PRODUCTION") {
        Sentry.captureException(error);
      }
    }
  }

  async trackEvent(
    name: string,
    userId: string,
    properties: any = {},
    isIndependent: boolean,
    isQuestUser: boolean = false,
    ignoreMAU: boolean = false
  ) {
    try {
      const response = await this.quest.track({
        entityId: this.entityId,
        name,
        userId,
        properties: properties,
        isIndependent,
        isQuestUser,
      }, { _body: { ignoreMAU } });
    } catch (error) {
      this.captureSentryException(error);
      console.error("Error tracking event:", error);
    }
  }
}

const createUrl = (
  apiString: any,
  BACKEND_URL: any,
  apikey: any,
  userId: any,
  token: any
) => {
  const url = `${BACKEND_URL}${apiString}`;
  const headers = {
    apiKey: apikey,
    userId: userId,
    token: token,
  };

  return {
    url,
    headers,
  };
};

const compressImage = async (file: File, maxSizeInMB: number = 1, preserveFormat: boolean = true): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      const MAX_WIDTH = 1920;
      const MAX_HEIGHT = 1080;
      
      let { width, height } = img;
      
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = width * ratio;
        height = height * ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx?.drawImage(img, 0, 0, width, height);
      
      let outputType = file.type;
      let fileName = file.name;
      
      if (!preserveFormat && file.type === 'image/png') {
        outputType = 'image/jpeg';
        fileName = fileName.replace(/\.png$/i, '.jpg');
      }
      
      let quality = 0.9;
      const maxSizeBytes = maxSizeInMB * 1024 * 1024;
      
      const tryCompress = () => {
        const useQuality = outputType === 'image/png' ? undefined : quality;
        
        canvas.toBlob((blob) => {
          if (blob && (blob.size <= maxSizeBytes || quality <= 0.1)) {
            const compressedFile = new File([blob], fileName, {
              type: outputType,
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          } else if (outputType === 'image/png' && blob && blob.size > maxSizeBytes) {
            outputType = 'image/jpeg';
            fileName = fileName.replace(/\.png$/i, '.jpg');
            quality = 0.8;
            tryCompress();
          } else {
            quality -= 0.1;
            tryCompress();
          }
        }, outputType, useQuality);
      };
      
      tryCompress();
    };
    
    img.onerror = () => {
      console.error('Error loading image for compression');
      resolve(file);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

const getFileSizeInMB = (file: File): number => {
  return file.size / (1024 * 1024);
};

export const uploadImageToBackend = async (
  file: any,
  BACKEND_URL: any,
  apikey: any,
  userId: any,
  token: any
) => {
  if (!file) {
    return null;
  }

  let processedFile = file;
  
  if (isImageFile(file) && getFileSizeInMB(file) > 1) {
    try {
      processedFile = await compressImage(file, 1, true);
    } catch (error) {
      console.error('Error compressing image:', error);
      processedFile = file;
    }
  }

  const { url, headers: baseHeaders } = createUrl(
    `api/upload-img`,
    BACKEND_URL,
    apikey,
    userId,
    token
  );
  const headers = {
    ...baseHeaders,
    "Content-Type": "multipart/form-data",
  };

  const formData = new FormData();
  formData.append("uploaded_file", processedFile);

  try {
    const res = await axios.post(url, formData, { headers });
    return res.data;
  } catch (error) {
    return null;
  }
};

export const extractGradientColors = (gradientString: any) => {
  if (!gradientString) {
    return null;
  }
  if (typeof gradientString !== 'string' || !gradientString.includes('gradient')) {
    return gradientString;
  }

  const gradient = gradientString.replace(/\s+/g, ' ').trim();

  const colorWithPositionPattern = /(#[a-fA-F0-9]{6}|#[a-fA-F0-9]{3}|rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)|hsl\(\s*\d+\s*,\s*\d+%?\s*,\s*\d+%?\s*\)|hsla\(\s*\d+\s*,\s*\d+%?\s*,\s*\d+%?\s*,\s*[\d.]+\s*\)|red|blue|green|yellow|orange|purple|pink|black|white|gray|grey|brown|cyan|magenta|lime|navy|maroon|olive|aqua|fuchsia|silver|teal)\s*(\d+%?|\d*\.?\d+%?)?/gi;

  const matches = [];
  let match: any;
  while ((match = colorWithPositionPattern.exec(gradient)) !== null) {
    matches.push(match);
  }
  const colors = [];

  matches.forEach(match => {
    const color = match[1];
    const position = match[2] || null;

    const numericValue = position ? parseFloat(position.toString().replace(/[%px]/g, '')) : null;

    colors.push({
      color: color,
      position: position,
      numericValue: numericValue,
      hasValue: position !== null
    });
  });

  const uniqueColors = colors.filter((item, index, self) =>
    index === self.findIndex(t => t.color === item.color && t.position === item.position)
  );

  const colorsWithValues = uniqueColors.filter(item => item.hasValue);

  let highestValueColor: any;

  if (colorsWithValues.length > 0) {
    highestValueColor = colorsWithValues.reduce((max, current) =>
      current.numericValue > max.numericValue ? current : max
    );
  } else {
    highestValueColor = uniqueColors[0];
  }

  return highestValueColor?.color;
}

export const hexToRgb = (hex) => {
  const bigint = parseInt(hex?.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `${r}, ${g}, ${b}`;
};

export function hexToGlassColor(color, opacity = 0.05) {
  if (!color) return ``;

  if (color.includes('gradient')) {
    color = extractGradientColors(color);
  }

  if (color.startsWith('rgb')) {
    return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  }

  if (!color.startsWith('#') && !color.startsWith('rgb')) {
    const tempEl = document.createElement('div');
    tempEl.style.color = color;
    document.body.appendChild(tempEl);

    const computedColor = getComputedStyle(tempEl).color;
    document.body.removeChild(tempEl);

    if (computedColor.startsWith('rgb')) {
      return computedColor.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    }

    return `rgba(0, 0, 0, ${opacity})`;
  }

  let hex = color.replace('#', '');

  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export const useGeneral = (serviceType: string) => {
  const {apiKey, entityId, apiType} = useContext(QuestContext.Context)
  return useMemo(() => new General(serviceType, apiType, apiKey, entityId), [serviceType, apiType, apiKey, entityId])
}

export default General;
